/* ElahRing — interaction layer. Keeps the Figma states (tabs, accordion,
   carousel dots, day-part rail) behaving as designed. */
(function () {
  'use strict';

  /* Elements inside `scope`, plus `scope` itself when it matches. The theme
     editor hands us a section wrapper, and a descendants-only selector would
     miss a section whose own root carries the hook. */
  function find(scope, sel) {
    var out = Array.prototype.slice.call(scope.querySelectorAll(sel));
    if (scope.matches && scope.matches(sel)) out.unshift(scope);
    return out;
  }

  /* Everything below binds per section rather than once per page. The theme
     editor replaces a section's markup wholesale on every settings change —
     add a card, edit a line of copy — and every listener bound to the old
     nodes goes with it. Until this existed the merchant's first edit silently
     killed that section's behaviour for the rest of the session: the day-part
     timeline stopped following the deck, the dots stopped tracking, the
     accordion stopped opening. Re-running against just the replaced subtree
     rebinds it without disturbing the sections still standing.

     Attribution, Klaviyo and the signup popup deliberately stay outside this
     function: they fire events rather than bind them, and re-running them on
     every edit would send duplicates. */
  function bindSection(scope) {
    /* --------------------------- FAQ accordion --------------------------- */
    find(scope, '.faq__item').forEach(function (item) {
      var btn = item.querySelector('.faq__q');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      });
    });

    /* --------------- Product swatches -> Shopify variant ------------------ */
    find(scope, '.swatches').forEach(function (group) {
      var form = group.closest('form');
      var input = form && form.querySelector('[data-variant-input]');
      var titleEl = form && form.querySelector('[data-variant-title]');
      var submit = form && form.querySelector('.product__cta');
      var json = form && form.querySelector('[data-variant-json]');
      var variants = [];
      if (json) { try { variants = JSON.parse(json.textContent); } catch (e) { variants = []; } }
      var swatches = group.querySelectorAll('.swatch');

      swatches.forEach(function (btn) {
        btn.addEventListener('click', function () {
          swatches.forEach(function (b) {
            b.classList.remove('is-active');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-checked', 'true');

          if (!variants.length) return;              // static mode: visual only
          var match = variants.filter(function (v) { return v.option1 === btn.dataset.value; })[0];
          if (!match) return;
          if (input) input.value = match.id;
          if (titleEl) titleEl.textContent = match.title;
          if (submit) submit.disabled = !match.available;
          if (window.history && history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            history.replaceState({}, '', url);
          }
        });
      });
    });

    /* ------------------- Finish swatch → product photo ------------------- */
    find(scope, '.product__card').forEach(function (card) {
      var img = card.querySelector('.product__media img');
      var swatches = Array.prototype.slice.call(card.querySelectorAll('.swatch'));
      if (!img || swatches.length < 2) return;

      var nameEl = card.querySelector('[data-variant-title]') || card.querySelector('.product__variant');
      var bodyEl = card.querySelector('.product__desc');
      var withPhoto = swatches.filter(function (s) { return s.dataset.src; });
      if (!withPhoto.length) return;

      /* Decode the other finishes up front. Without this the first click shows
         an empty box for a whole network round trip, which reads as a bug. */
      withPhoto.forEach(function (s) {
        var pre = new Image();
        if (s.dataset.srcset) pre.srcset = s.dataset.srcset;
        pre.sizes = img.sizes || '';
        pre.src = s.dataset.src;
      });

      swatches.forEach(function (btn) {
        btn.addEventListener('click', function () {
          /* srcset has to be cleared first: a stale candidate list outranks the
             new src and the browser would keep painting the old finish. */
          if (btn.dataset.src) {
            img.srcset = btn.dataset.srcset || '';
            img.src = btn.dataset.src;
          }
          if (nameEl && btn.dataset.name) nameEl.textContent = btn.dataset.name;
          if (bodyEl && btn.dataset.body) bodyEl.innerHTML = btn.dataset.body;
        });
      });
    });

    /* ----------------------- Colour swatch → photo ----------------------- */
    (function () {
      var media = document.querySelector('[data-spec-media]');
      var dots = find(scope, '.specs__swatch');
      if (!dots.length) return;
      /* Desktop shows one photo beside the list; mobile shows one per card, and
         the swatches live in the Materials card — swap whichever is on screen. */
      var targets = [];
      if (media && media.querySelector('img')) targets.push(media.querySelector('img'));
      var ownCard = dots[0].closest('.specs__row');
      if (ownCard && ownCard.querySelector('.specs__card-img')) targets.push(ownCard.querySelector('.specs__card-img'));
      if (!targets.length || dots.length < 2) return;

      /* Warm the other photos so the first switch is not a blank frame. */
      dots.forEach(function (d) {
        if (!d.dataset.src) return;
        var pre = new Image();
        pre.src = d.dataset.src;
      });

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          if (dot.classList.contains('is-active')) return;
          dots.forEach(function (d) {
            d.classList.toggle('is-active', d === dot);
            d.setAttribute('aria-pressed', String(d === dot));
          });
          applySpecVideo(dot, media);
          if (ownCard) applySpecVideo(dot, ownCard.querySelector('[data-spec-vid-host]') || ownCard);
          if (!dot.dataset.src) return;
          targets.forEach(function (t) { t.classList.add('is-swapping'); });
          var next = new Image();
          next.onload = next.onerror = function () {
            targets.forEach(function (t) {
              if (dot.dataset.srcset) t.srcset = dot.dataset.srcset;
              t.src = dot.dataset.src;
              t.alt = dot.getAttribute('aria-label') || t.alt;
              t.classList.remove('is-swapping');
            });
          };
          next.src = dot.dataset.src;
        });
      });
    })();

    /* --------------------- Swipe indicators (mobile) ----------------------
       Any [data-dots] binds to the scroller immediately before it — one dot per
       child, the nearest to centre wins. Used by the spec deck and the day-part
       cards; adding it to another row needs no extra JS. */
    find(scope, '[data-dots]').forEach(function (dots) {
      var pips = Array.prototype.slice.call(dots.children);
      if (!pips.length) return;

      /* The scroller is whichever element nearby holds one child per dot: a
         preceding sibling where the dots sit after the row (the day-part deck),
         or the parent where they sit inside it (the spec list). Marquee clones
         are duplicates of real items and must not be counted.

         Picking by "does it overflow" instead breaks whenever the row happens to
         fit — which is precisely when the dots should disappear, not walk up the
         page and latch onto something unrelated. Three day-part cards fill the
         row exactly on a desktop, and that was enough to leave the dots dark. */
      function countable(el) {
        return Array.prototype.slice.call(el.children).filter(function (c) {
          return c !== dots && !c.hasAttribute('data-marquee-clone');
        }).length;
      }
      var list = dots.previousElementSibling;
      while (list && countable(list) !== pips.length) list = list.previousElementSibling;
      if (!list && dots.parentElement && countable(dots.parentElement) === pips.length) {
        list = dots.parentElement;
      }
      if (!list) return;

      /* Position along the scroll range rather than the item nearest the centre.
         Where several items fit at once the first and last can never be centred,
         so a nearest-to-centre rule leaves the dots at both ends permanently
         dark; this also keeps the day-part dots saying the same thing as the
         timeline above them. With one item per row the two rules coincide. */
      function sync() {
        var max = list.scrollWidth - list.clientWidth;
        /* A row that fits has nothing to page through, and dots that can never
           move read as broken. */
        dots.style.display = max < 1 ? 'none' : '';
        var best = max < 1 ? 0 : Math.round(list.scrollLeft / max * (pips.length - 1));
        pips.forEach(function (p, i) { p.classList.toggle('is-active', i === best); });
      }

      /* sync() is now two reads and a division, so there is nothing left worth
         deferring a frame for — and deferring only holds while frames keep
         coming. */
      list.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      /* Hiding the row removes the only event that could bring it back, so run
         again once images have settled and the real width is known. */
      window.addEventListener('load', sync);
      sync();
    });

    /* --------------------------- Spec accordion ---------------------------
       One row open at a time, and opening one swaps the photo beside the list —
       the rows carry their own image, which on mobile sits on the card instead. */
    /* Points a spec panel's video layer at whatever the visitor just picked.
       An element with no clip for this breakpoint fades the layer out and leaves
       the photograph, so a mixed set of rows behaves sensibly. */
    function applySpecVideo(source, host) {
      if (!host) return;
      var layer = host.querySelector('[data-spec-vid]');
      if (!layer) return;
      var video = layer.querySelector('video');
      if (!video) return;
      var phone = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      var url = (phone && source.getAttribute('data-video-m')) ||
                source.getAttribute('data-video-d') ||
                source.getAttribute('data-video-m') || '';
      if (url === (layer.dataset.current || '')) return;
      layer.dataset.current = url;
      layer.classList.remove('is-ready');
      if (!url) { try { video.pause(); } catch (e) {} video.removeAttribute('src'); return; }
      video.src = url;
      video.muted = true;
      video.loop = true;
      video.setAttribute('playsinline', '');
      /* Shown as soon as it has a frame to show, not once playback is confirmed.
         Waiting for "playing" meant that on any connection where the file was
         slow to buffer the layer stayed at opacity 0 for good — the visitor
         clicked a feature and the panel simply never changed. A frame on screen,
         even a still one, is the feedback that the click did something.
         loadeddata fires as soon as the first frame is decodable; the poster
         covers the gap before that. */
      video.addEventListener('loadeddata', function () { layer.classList.add('is-ready'); }, { once: true });
      video.addEventListener('playing', function () { layer.classList.add('is-ready'); }, { once: true });
      video.load();
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* blocked — the frame is still up */ });
    }

    (function () {
      var rows = find(scope, '.specs__row');
      if (!rows.length) return;
      var media = document.querySelector('[data-spec-media]');
      var big = media && media.querySelector('img');
      /* What the frame shows when the open row has no photo of its own. */
      var baseSrc = big && big.getAttribute('src');
      var baseSrcset = big && big.getAttribute('srcset');

      /* Warm the row photos so the first open is not a blank frame. */
      rows.forEach(function (r) {
        if (!r.dataset.src) return;
        var pre = new Image();
        pre.src = r.dataset.src;
      });

      function show(row) {
        applySpecVideo(row, media);
        /* A row can carry a clip and no photograph — the four feature rows do.
           Falling through here left whatever the previous row put in the frame,
           so opening one of them appeared to do nothing at all until its video
           had buffered. Reset to the section's own image instead, which is what
           the clip then fades in over. */
        if (!row.dataset.src) {
          if (big && baseSrc && big.getAttribute('src') !== baseSrc) {
            if (baseSrcset) big.srcset = baseSrcset; else big.removeAttribute('srcset');
            big.src = baseSrc;
          }
          return;
        }
        if (!big) return;
        big.classList.add('is-swapping');
        var next = new Image();
        next.onload = next.onerror = function () {
          if (row.dataset.srcset) big.srcset = row.dataset.srcset;
          big.src = row.dataset.src;
          big.classList.remove('is-swapping');
        };
        next.src = row.dataset.src;
      }

      rows.forEach(function (row) {
        var btn = row.querySelector('.specs__q');
        if (!btn) return;
        btn.addEventListener('click', function () {
          var willOpen = !row.classList.contains('is-open');
          rows.forEach(function (r) {
            r.classList.remove('is-open');
            var b = r.querySelector('.specs__q');
            if (b) b.setAttribute('aria-expanded', 'false');
          });
          if (willOpen) {
            row.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            show(row);
          }
        });
      });

      /* Whichever row starts open owns the first photo. */
      var start = rows.filter(function (r) { return r.classList.contains('is-open'); })[0];
      if (start) show(start);

      /* The frame is rendered already pointing at the open row's clip, so kick it
         off here rather than leaving it to the lazy mounter — this layer is no
         longer in that list precisely because its source changes as rows are
         picked. */
      (function () {
        var layer = media && media.querySelector('[data-spec-vid]');
        var v = layer && layer.querySelector('video');
        if (!v || !v.getAttribute('src')) return;
        var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (still) return;
        function reveal() { layer.classList.add('is-ready'); }
        v.addEventListener('loadeddata', reveal, { once: true });
        v.addEventListener('playing', reveal, { once: true });
        v.preload = 'auto';
        v.muted = true;
        v.load();
        var pr = v.play();
        if (pr && pr.catch) pr.catch(function () { /* blocked — the still stays */ });
      })();
    })();

    /* --------------------------- Ring picker -----------------------------
       Fills the three line item properties at submit time rather than keeping
       them in sync as the customer clicks about — there is one moment that
       matters and it is this one, so there is no way for the posted values and
       the visible state to disagree. */
    (function () {
      function ringId() {
        /* Only has to be unique within a cart; the point is that two otherwise
           identical rings do not merge into one line of quantity 2. */
        try {
          if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
        } catch (e) { /* older browser */ }
        return 'r-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      }

      find(scope, '[data-ring-picker]').forEach(function (root) {
        var form = root.querySelector('form');
        if (!form) return;
        var sizeError = root.querySelector('[data-size-error]');
        var variantInput = root.querySelector('[data-variant-id]');
        var priceEl = root.querySelector('[data-price]');
        var cIndex = Number(root.dataset.colourIndex || 0);
        var sIndex = Number(root.dataset.sizeIndex || 1);
        var tbc = root.dataset.tbc || 'TBC';
        var variants = [];
        try {
          variants = JSON.parse(root.querySelector('[data-variants]').textContent);
        } catch (e) { /* no variants means nothing to add; the CTA stays inert */ }

        /* Colour and size are two axes of one variant here, not a variant and a
           property. That is the product's own shape and it is worth keeping:
           it is what gives the store stock per size. The kit route is the case
           it cannot express — nobody knows the size yet and a variant cannot be
           blank — so the product carries one size value meaning "not yet", and
           that is the variant that route sells. The real size arrives later, on
           the order metafield, because a line item's properties are frozen once
           the order exists. */
        function variantFor(colour, size) {
          for (var i = 0; i < variants.length; i++) {
            var o = variants[i].o || [];
            if (o[cIndex] === colour && o[sIndex] === size) return variants[i];
          }
          return null;
        }
        function chosenColour() {
          var on = root.querySelector('[data-colour]:checked');
          return on ? on.value : (variants[0] && variants[0].o ? variants[0].o[cIndex] : '');
        }
        var propSize = root.querySelector('[data-prop-size]');
        var propFlow = root.querySelector('[data-prop-flow]');
        var propId = root.querySelector('[data-prop-ringid]');

        function isDirect() {
          var on = root.querySelector('[data-flow]:checked');
          return !!on && on.value === 'direct';
        }

        function chosenSize() {
          var on = root.querySelector('[data-size]:checked');
          return on ? on.value : '';
        }

        /* Which variant the current pair of answers points at, and what it
           costs. Resolved on every change so the price and the sold-out state
           belong to the thing actually about to be bought. */
        function current() {
          var direct = isDirect();
          var size = direct ? chosenSize() : tbc;
          if (!size) return null;
          return variantFor(chosenColour(), size);
        }

        var addError = root.querySelector('[data-add-error]');

        function refresh() {
          var v = current();
          if (variantInput) variantInput.value = v ? v.id : '';
          if (priceEl && v && v.p != null) {
            priceEl.textContent = money(v.p);
          }
          /* The picture used to be swapped here, by writing the variant's
             image over the first slide's src. The gallery owns that now — it
             filters the track to the chosen finish — and doing both would have
             this one overwrite a slide the filter had just paged to. */
          /* A colour whose chosen size is gone should say so before the click,
             not after the cart refuses it. */
          if (addError && v && !v.a) {
            addError.textContent = root.dataset.soldOutNote || '';
            addError.hidden = false;
          } else if (addError && v) {
            addError.hidden = true;
          }
        }

        /* Shopify hands prices back in cents; the page has no money filter. */
        function money(cents) {
          return '$' + (Number(cents) / 100).toFixed(2);
        }

        /* Only the open card shows its contents. Driven from here rather than
           from :has() in the stylesheet so the closed panel is genuinely out of
           the document — hidden by CSS alone it would still take tab stops, and
           a size you can reach but cannot see is worse than no size at all. */
        function sync() {
          var direct = isDirect();
          root.querySelectorAll('[data-opt]').forEach(function (card) {
            var on = card.querySelector('[data-flow]');
            var body = card.querySelector('[data-opt-body]');
            if (body) body.hidden = !on || !on.checked;
          });
          if (sizeError && !direct) sizeError.hidden = true;
        }

        root.querySelectorAll('[data-flow]').forEach(function (r) {
          r.addEventListener('change', function () { sync(); refresh(); });
        });
        var colourName = root.querySelector('[data-colour-name]');
        root.querySelectorAll('[data-colour]').forEach(function (r) {
          r.addEventListener('change', function () {
            /* The heading names the chosen finish, so it has to follow it. */
            if (colourName) colourName.textContent = r.value;
            refresh();
          });
        });

        /* The heading is the label, so the rest of a closed card would be dead
           to the pointer otherwise — and a card that looks like a button has to
           behave like one across its whole face. Clicks that land on a control
           inside the open panel are left alone. */
        root.querySelectorAll('[data-opt]').forEach(function (card) {
          card.addEventListener('click', function (e) {
            if (e.target.closest('label, a, button, input, select')) return;
            var on = card.querySelector('[data-flow]');
            if (!on || on.checked) return;
            on.checked = true;
            /* Dispatch rather than calling sync() here. Setting .checked from
               script fires no change event, so this path used to update the
               cards' open/closed look and stop — refresh() never ran, the
               resolved variant kept whatever size had been picked on the other
               route, and someone who chose #11 and then pressed "I don't know
               the size" still checked out with #11. Going through the event
               means there is one path, and anything else listening hears it. */
            on.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        root.querySelectorAll('[data-size]').forEach(function (r) {
          r.addEventListener('change', function () {
            if (sizeError) sizeError.hidden = true;
            refresh();
          });
        });
        sync();
        refresh();

        var added = root.querySelector('[data-added]');
        var addedText = root.querySelector('[data-added-text]');
        var cta = form.querySelector('button[type="submit"]');
        var kitVariant = root.dataset.kitVariant || '';
        var busy = false;

        function refreshCartCount() {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('[data-cart-count]').forEach(function (el) {
                el.textContent = cart.item_count;
                el.hidden = cart.item_count === 0;
              });
              var link = document.querySelector('[data-cart-link]');
              if (link) link.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' items');
              return cart;
            });
        }

        /* Intercepted rather than posted natively: the kit route puts two
           products in at once, which a product form cannot express. */
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (busy) return;
          /* The sticky bar asks for checkout by setting this before submitting
             the panel's own form. Read once and cleared here, so a press that
             fails validation cannot send the next, unrelated add straight to
             checkout. */
          var goCheckout = root.dataset.afterAdd === 'checkout';
          root.dataset.afterAdd = '';

          var direct = isDirect();
          if (direct && !chosenSize()) {
            if (sizeError) sizeError.hidden = false;
            var first = root.querySelector('[data-size]');
            if (first) first.focus();
            return;
          }

          var v = current();
          if (!v) return;
          if (!v.a) {
            if (addError) {
              addError.textContent = root.dataset.soldOutNote || '';
              addError.hidden = false;
            }
            return;
          }

          var props = {};
          props[propSize.name.replace(/^properties\[|\]$/g, '')] =
            direct ? chosenSize() : root.dataset.pendingLabel;
          props[propFlow.name.replace(/^properties\[|\]$/g, '')] =
            direct ? root.dataset.directLabel : root.dataset.kitLabel;
          props._ring_id = ringId();

          var items = [{ id: Number(v.id), quantity: 1, properties: props }];

          busy = true;
          if (cta) { cta.classList.add('is-working'); cta.setAttribute('aria-busy', 'true'); }
          if (addError) addError.hidden = true;
          if (added) added.hidden = true;

          /* One kit per cart, not per ring — check what is already in there
             before deciding to add another. */
          var prep = (!direct && kitVariant)
            ? fetch('/cart.js', { headers: { Accept: 'application/json' } })
                .then(function (r) { return r.json(); })
                .then(function (cart) {
                  var has = (cart.items || []).some(function (i) {
                    return String(i.variant_id) === String(kitVariant);
                  });
                  if (!has) items.push({ id: Number(kitVariant), quantity: 1 });
                  return items.length > 1;
                })
                .catch(function () { return false; })
            : Promise.resolve(false);

          prep.then(function (kitAdded) {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: items })
            }).then(function (r) {
              if (!r.ok) throw new Error('add failed');
              return r.json();
            }).then(function () {
              return refreshCartCount().catch(function () {}).then(function () {
                if (added && addedText) {
                  addedText.textContent = kitAdded
                    ? (root.dataset.kitAddedNote || '')
                    : (root.dataset.addedNote || '');
                  added.hidden = false;
                }
                /* The id is per ring, so the next one must not reuse this one. */
                if (propId) propId.value = '';
                if (goCheckout) {
                  var croot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
                  window.location.href = croot.replace(/\/$/, '') + '/checkout';
                  return;
                }
                /* Leaving for the cart is a setting, because the panel is also
                   how a second ring gets configured — sending every add to the
                   cart makes buying two rings a round trip each time. */
                if (root.hasAttribute('data-go-to-cart')) {
                  window.location.href = root.dataset.cartUrl || '/cart';
                }
              });
            });
          }).catch(function () {
            if (addError) {
              addError.textContent = root.dataset.addErrorNote || '';
              addError.hidden = false;
            }
          }).then(function () {
            busy = false;
            if (cta) { cta.classList.remove('is-working'); cta.removeAttribute('aria-busy'); }
          });
        });
      });
    })();

    /* ------------------- Removing the last ring -------------------------
       Taking the last ring out has to take the kit with it, and has to do it
       in the same breath. Letting the remove link navigate and cleaning up
       afterwards worked, but it cost two page loads and a round trip — about
       three seconds during which the cart sat there showing a free kit and an
       enabled checkout button. One update call carries both lines instead. */
    (function () {
      var form = document.querySelector('[data-cart-form]');
      if (!form) return;
      var kitKey = form.getAttribute('data-kit-key') || '';
      var ringCount = Number(form.getAttribute('data-ring-count') || 0);
      if (!kitKey || ringCount !== 1) return;

      form.addEventListener('click', function (e) {
        var link = e.target.closest ? e.target.closest('[data-ring-line]') : null;
        if (!link) return;
        var ringKey = link.getAttribute('data-line-remove');
        if (!ringKey) return;
        e.preventDefault();
        if (link.dataset.busy) return;
        link.dataset.busy = '1';

        var updates = {};
        updates[ringKey] = 0;
        updates[kitKey] = 0;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) {
          if (!r.ok) throw new Error('update failed');
          window.location.reload();
        }).catch(function () {
          /* Fall back to the plain link; the orphan sweep below still runs. */
          delete link.dataset.busy;
          window.location.href = link.href;
        });
      });
    })();

    /* --------------------- Orphan sizing kit ---------------------------
       The kit is free and only earns its place beside a ring. A customer
       found that adding a ring, then removing the ring from the cart, left
       the kit behind — a parcel the shop packs and posts for nothing. The
       cart section marks that state and hands over the line key; dropping
       the line is what actually closes it, because a disabled checkout
       button does nothing for someone who types /checkout instead. */
    (function () {
      var flag = document.querySelector('[data-kit-orphan]');
      if (!flag) return;
      var key = flag.getAttribute('data-kit-orphan');
      if (!key) return;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).then(function (r) {
        /* Reload rather than patch the DOM: the totals, the count in the
           header and the empty-cart copy are all server rendered. */
        if (r.ok) window.location.reload();
      }).catch(function () { /* the disabled checkout button still holds */ });
    })();

    /* ----------------------- Segmented tab control ----------------------- */
    find(scope, '.tabs').forEach(function (group) {
      var items = Array.prototype.slice.call(group.querySelectorAll('.tabs__item'));
      var panels = group.parentElement.querySelectorAll('.tabs__panel');
      items.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          items.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
          panels.forEach(function (p, j) { p.classList.toggle('is-active', i === j); });
          /* The panel that just appeared may hold a clip that has never been
             mounted — it was display:none until this click. */
          document.dispatchEvent(new CustomEvent('elah:revealed'));
        });
      });
    });

    /* --------------------------- Pain carousel --------------------------- */
    find(scope, '[data-carousel]').forEach(function (root) {
      var track = root.querySelector('.carousel__track');
      var dots = Array.prototype.slice.call(root.querySelectorAll('.carousel__dots button'));
      var slides = root.querySelectorAll('.carousel__slide');
      var index = 0;

      function go(next) {
        index = (next + dots.length) % dots.length;
        if (track && slides.length > 1) {
          track.style.transform = 'translateX(' + (-index * 100) + '%)';
        }
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
      }

      root.querySelectorAll('.carousel__arrow').forEach(function (btn) {
        btn.addEventListener('click', function () {
          go(index + (btn.dataset.dir === 'next' ? 1 : -1));
        });
      });
      dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });
      if (track) track.style.transition = 'transform .45s cubic-bezier(.4,0,.2,1)';

      /* Swipe. The arrows are hidden below 768px, which left the dots as the only
         way through the deck — on a phone the obvious gesture did nothing at all.
         The track is transform-driven rather than a scroller, so the gesture has
         to be read by hand.

         touch-action: pan-y in the stylesheet keeps vertical scrolling with the
         page; only the horizontal component is claimed here. */
      if (track && slides.length > 1) {
        var x0 = 0, y0 = 0, dx = 0, dy = 0, dragging = false;

        track.addEventListener('touchstart', function (e) {
          if (e.touches.length !== 1) return;
          x0 = e.touches[0].clientX;
          y0 = e.touches[0].clientY;
          dx = dy = 0;
          dragging = true;
        }, { passive: true });

        track.addEventListener('touchmove', function (e) {
          if (!dragging) return;
          dx = e.touches[0].clientX - x0;
          dy = e.touches[0].clientY - y0;
          /* Once it is clearly a vertical scroll, stop tracking — otherwise a
             slightly slanted scroll ends up flicking the carousel. */
          if (Math.abs(dy) > Math.abs(dx)) dragging = false;
        }, { passive: true });

        track.addEventListener('touchend', function () {
          if (!dragging) return;
          dragging = false;
          /* A quarter of the viewport, capped — enough that a scroll that drifts
             sideways does not count, small enough that a real flick always does. */
          var threshold = Math.min(80, window.innerWidth * 0.25);
          if (Math.abs(dx) < threshold) return;
          go(index + (dx < 0 ? 1 : -1));
        });
      }
    });

    /* -------------------- Day-part rail follows the tab ------------------- */
    find(scope, '.daypart__tabs').forEach(function (root) {
      var labels = Array.prototype.slice.call(root.querySelectorAll('.daypart__labels li'));
      var thumb = root.querySelector('.daypart__thumb');
      if (!thumb || !labels.length) return;

      function move(el) {
        var railBox = root.querySelector('.daypart__rail').getBoundingClientRect();
        var box = el.getBoundingClientRect();
        thumb.style.width = box.width + 'px';
        thumb.style.transform = 'translateX(' + (box.left - railBox.left) + 'px)';
      }

      /* The labels and the deck are the same sequence shown two ways, so they
         have to move together: naming a time should carry you to that card, and
         arriving at a card should name the time. Before this the labels only
         moved their own underline — the deck below them never listened, and
         scrolling the deck left the wrong time marked. */
      var deck = root.parentElement.querySelector('.daypart__grid');
      var cards = deck ? Array.prototype.slice.call(deck.children) : [];

      /* One label per card: label k names card k. Six labels over three cards
         spreads the scroll range across six steps the deck cannot make, so the
         second card lands on the fourth time of day. A label with no card behind
         it can never be arrived at either, so it comes out of the row — and
         comes back on its own the moment that card is added. */
      if (cards.length && cards.length < labels.length) {
        labels.splice(cards.length).forEach(function (el) { el.style.display = 'none'; });
      }
      var last = labels.length - 1;

      /* Position along the scroll range, not the card sitting at the left edge.
         The deck stops once its final card is flush right, so on a wide screen
         three of the six never reach the left edge at all — marking the leading
         card would leave the last times of day permanently unreachable. Scroll
         progress maps onto the labels end to end: nothing scrolled is the first
         time of day, scrolled to the end is the last. On the phone, where one
         card fills the row, the two rules coincide exactly. */
      function span() { return deck.scrollWidth - deck.clientWidth; }

      function activate(i) {
        if (i < 0 || i >= labels.length) return;
        labels.forEach(function (l, j) { l.classList.toggle('is-active', j === i); });
        move(labels[i]);
        /* The label row scrolls too on narrow screens; keep the marked one in
           sight rather than underlining something off the edge. */
        var row = root.querySelector('.daypart__labels');
        if (row && row.scrollWidth > row.clientWidth + 1) {
          var l = labels[i];
          var want = l.offsetLeft - (row.clientWidth - l.offsetWidth) / 2;
          row.scrollTo({ left: Math.max(0, want), behavior: 'smooth' });
        }
      }

      labels.forEach(function (el, i) {
        el.addEventListener('click', function () {
          activate(i);
          if (deck && last > 0 && span() > 0) {
            deck.scrollTo({ left: Math.round(span() * i / last), behavior: 'smooth' });
          }
        });
      });

      if (deck && cards.length && last > 0) {
        /* Read straight out of the scroll handler rather than deferred into
           requestAnimationFrame: this is one division, and the deferred version
           is only correct as long as frames keep coming — which is exactly the
           assumption that fails once the page has settled and stopped painting. */
        deck.addEventListener('scroll', function () {
          var max = span();
          if (max < 1) return;
          var i = Math.round(deck.scrollLeft / max * last);
          if (!labels[i] || labels[i].classList.contains('is-active')) return;
          activate(i);
        }, { passive: true });
      }

      /* The rail sits outside the label row and does not scroll with it, so a
         thumb placed once goes stale the moment the row moves: activate()
         measures the label, then scrolls the row smoothly underneath it, and
         the label slides out from under the mark it was given. From MIDDAY on
         — the first label the row has to scroll to reach — the underline was
         left sitting over the next name along. Re-placing it on every scroll
         frame keeps the two together for the whole animation. */
      var labelRow = root.querySelector('.daypart__labels');
      if (labelRow) {
        labelRow.addEventListener('scroll', function () {
          var on = labelRow.querySelector('.is-active');
          if (on) move(on);
        }, { passive: true });
      }

      thumb.style.transition = 'transform .35s cubic-bezier(.4,0,.2,1), width .35s cubic-bezier(.4,0,.2,1)';
      var active = root.querySelector('.daypart__labels .is-active') || labels[0];
      requestAnimationFrame(function () { move(active); });

      /* move() reads two bounding rects then writes styles, so an unthrottled
         resize handler forces a synchronous layout on every event. Coalesce to
         one per frame. */
      var pending = false;
      window.addEventListener('resize', function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          move(root.querySelector('.daypart__labels .is-active') || labels[0]);
        });
      });
    });

    /* ---------------------------- Mobile menu ---------------------------- */
    var burger = find(scope, '.nav__burger')[0];
    var drawer = find(scope, '.nav__drawer')[0];
    if (burger && drawer) {
      burger.addEventListener('click', function () {
        var open = drawer.hasAttribute('hidden');
        if (open) drawer.removeAttribute('hidden'); else drawer.setAttribute('hidden', '');
        burger.setAttribute('aria-expanded', String(open));
        burger.classList.toggle('is-open', open);
      });
    }

    /* ----------------------------- Marquee row -----------------------------
       The blocks are printed twice; once scrollLeft passes the first copy we
       subtract its width, which lands on an identical frame so the jump is
       invisible. Anything the visitor does — hover, drag, tab — pauses it, and
       an off-screen row stops entirely rather than burning frames. */
    find(scope, '[data-marquee]').forEach(function (row) {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var speed = parseFloat(row.dataset.speed) || 35;   // px per second
      var paused = false;
      var visible = true;
      var last = null;
      var resume;

      /* The wrap point is the width of ONE printed copy, measured as the distance
         from the first card to the first clone — scrollWidth/2 was wrong because
         the gap between the two copies belongs to neither half.

         More importantly, a copy has to be shorter than the scrollable distance
         or the wrap can never happen: with 5 cards the row is ~2900px, but a
         1920 viewport can only scroll 996px, so scrollLeft never reached the old
         1458px threshold and the strip simply ran to its end and stopped. Narrow
         screens looped because they can scroll much further. Clone until there is
         room, then wrap on the real copy width. */
      var originals = Array.prototype.slice.call(row.children).filter(function (el) {
        return !el.hasAttribute('data-marquee-clone');
      });

      function copyWidth() {
        var firstClone = row.querySelector('[data-marquee-clone]');
        if (!firstClone || !originals.length) return row.scrollWidth / 2;
        return firstClone.offsetLeft - originals[0].offsetLeft;
      }

      function topUp() {
        if (!originals.length) return;
        var guard = 0;
        while (row.scrollWidth < row.clientWidth + copyWidth() + 2 && guard++ < 12) {
          originals.forEach(function (el) {
            var c = el.cloneNode(true);
            c.setAttribute('data-marquee-clone', '');
            c.setAttribute('aria-hidden', 'true');
            row.appendChild(c);
          });
        }
      }

      topUp();
      window.addEventListener('resize', topUp);
      /* Card widths settle only once the photos have their intrinsic size. */
      window.addEventListener('load', topUp);

      function tick(now) {
        if (last === null) last = now;
        var dt = (now - last) / 1000;
        last = now;
        if (!paused && visible && dt < 0.5) {
          row.scrollLeft += speed * dt;
          var w = copyWidth();
          if (w > 0) { while (row.scrollLeft >= w) row.scrollLeft -= w; }
        }
        window.requestAnimationFrame(tick);
      }

      function pause() { paused = true; window.clearTimeout(resume); }
      function play(delay) {
        window.clearTimeout(resume);
        resume = window.setTimeout(function () { paused = false; last = null; }, delay || 0);
      }

      row.addEventListener('pointerenter', pause);
      row.addEventListener('pointerleave', function () { play(300); });
      row.addEventListener('pointerdown', pause);
      window.addEventListener('pointerup', function () { play(1200); });
      row.addEventListener('touchstart', pause, { passive: true });
      row.addEventListener('touchend', function () { play(1800); });
      row.addEventListener('focusin', pause);
      row.addEventListener('focusout', function () { play(600); });
      /* A manual wheel/trackpad scroll also counts as "leave it alone". */
      row.addEventListener('wheel', function () { pause(); play(1500); }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) last = null;
        }, { threshold: 0 }).observe(row);
      }

      window.requestAnimationFrame(tick);
    });

    /* ------------------------- Deferred hero video -------------------------
       The <video> ships with preload="none" and no autoplay, so nothing is
       fetched during the initial load — the poster image carries the first
       paint. Once the page is done, and only if the connection looks capable,
       the video is told to load and play. */
    (function () {
      var wraps = find(scope, '[data-hero-video], [data-lazy-video]');
      if (!wraps.length) return;

      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
      /* 3g deliberately not in this list. effectiveType is derived from observed
         round-trip time, so a VPN, a busy wifi or a middling 4G cell all report
         "3g" on a connection that plays video fine — and the hero then silently
         never moves, which is what happened here. The file is preload="none" and
         only starts 300ms after load, so it cannot compete with LCP anyway; an
         explicit Data Saver choice and a genuinely unusable 2g link are the only
         signals worth refusing on. */
      var slow = conn.saveData === true || /(^|\W)(slow-2g|2g)(\W|$)/.test(conn.effectiveType || '');
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (slow || still) return;   // poster stays, and that is a fine hero

      /* A row of cards can hold half a dozen clips, so one that is nowhere near
         the screen should not be fetched yet. This was an IntersectionObserver
         and it simply never fired for these wrappers — they sit at opacity 0
         until playback starts, and the callback never arrived even with the
         element square in the viewport. Measuring the rectangle costs nothing at
         this scale and cannot silently do nothing. */
      /* 900 was far too generous. A phone viewport is ~820 tall, so anything in
         the first 1700px counted as "near" — which on this homepage is the card
         deck right under the hero, and its clip is 563 KB. That download starts
         while the hero image is still arriving and takes the bandwidth with it.
         200 means a card starts loading about a flick before it is reached. */
      var MARGIN = 200;
      function near(wrap) {
        if (wrap.hasAttribute('data-hero-video')) return true;   // above the fold
        var r = wrap.getBoundingClientRect();
        var h = window.innerHeight || document.documentElement.clientHeight || 0;
        return r.bottom > -MARGIN && r.top < h + MARGIN;
      }

      function start() {
        /* Only the wrapper the breakpoint actually shows — offsetParent is null
           for the hidden one, so the other file is never fetched. */
        wraps.forEach(function (wrap) {
          var video = wrap.querySelector('video');
          if (!video) return;
          /* The spec panel ships its layer empty and fills it when a row or a
             colour is picked. Nothing to load until then. */
          if (!video.currentSrc && !video.querySelector('source') && !video.src) return;
          if (!near(wrap)) return;
          var shown = wrap.offsetParent !== null;
          if (!shown) { video.pause(); return; }
          if (video.dataset.started) {
            /* Already mounted, so there is nothing to load — but it may well be
               paused, because the line above pauses it whenever its panel goes
               display:none. Switching tabs away and back did exactly that and
               then hit the bare `return` that used to live here, so the clip
               came back frozen on its last frame and never moved again. The
               "pause" handler further down could not rescue it either: at the
               instant we pause it the wrapper is already hidden, and that
               handler bails on hidden wrappers by design.

               The guard exists to stop a second load(), not to stop playing. */
            if (video.paused) {
              var again = video.play();
              if (again && again.catch) again.catch(function () { /* blocked — poster stays */ });
            }
            return;
          }
          video.dataset.started = '1';
          video.preload = 'auto';
          video.muted = true;
          video.setAttribute('playsinline', '');
          /* The hero nests a fade layer inside the wrapper; a card carries the
             fade on the wrapper itself. */
          var box = wrap.querySelector('.hero__video') || wrap;
          /* Same reasoning as the spec panel: reveal on the first decoded frame
             rather than on confirmed playback, so a slow file degrades to a still
             instead of to nothing. */
          video.addEventListener('loadeddata', function () {
            box.classList.add('is-ready');
          }, { once: true });
          video.addEventListener('playing', function () {
            box.classList.add('is-ready');
          }, { once: true });
          video.load();
          var p = video.play();
          if (p && p.catch) p.catch(function () { /* blocked — the poster remains */ });

          /* Phones pause background video freely — on a tab switch, on an
             incoming call, when memory gets tight — and never resume on their
             own. Each of those fires "pause" on an element nobody asked to
             pause, so nudge it back if it is still the one on screen. */
          video.addEventListener('pause', function () {
            if (!video.dataset.started) return;
            if (wrap.offsetParent === null) return;      // legitimately hidden now
            if (document.hidden) return;                 // resumed by the handler below
            window.setTimeout(function () {
              if (video.paused && wrap.offsetParent !== null && !document.hidden) {
                var r = video.play();
                if (r && r.catch) r.catch(function () {});
              }
            }, 400);
          });
        });
      }

      /* Coming back to the tab. Anything that was running and is on screen picks
         up where it left off. */
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) return;
        window.setTimeout(start, 200);
        wraps.forEach(function (wrap) {
          var v = wrap.querySelector('video');
          if (!v || !v.dataset.started || wrap.offsetParent === null) return;
          if (!v.paused) return;
          var r = v.play();
          if (r && r.catch) r.catch(function () {});
        });
      });

      /* Nothing starts until the page has finished loading. A clip that begins
         downloading while the hero image is still in flight competes for the
         same few hundred kbit/s and pushes LCP out — the whole point of these
         being lazy is that they cost nothing until they are wanted. */
      if (document.readyState === 'complete') window.setTimeout(start, 200);
      else window.addEventListener('load', function () { window.setTimeout(start, 200); });

      /* Re-check as the page moves. rAF-throttled, and every wrapper drops out
         of the loop for good once it has started. */
      var queued = false;
      function onScroll() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () { queued = false; start(); });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      /* Something that was display:none is now on screen — a tab panel. The
         observer above catches this on its own; this is the fallback for
         browsers without IntersectionObserver. */
      document.addEventListener('elah:revealed', function () { window.setTimeout(start, 60); });
      /* Rotating the phone can cross the breakpoint — start the other one then. */
      window.addEventListener('resize', function () { window.setTimeout(start, 200); });
    })();

    bindVideoPlay(scope);
    bindCountdown(scope);
    bindRingSizeForm(scope);
    bindStickyBuy(scope);
    bindSupportDone(scope);
    bindFinePrint(scope);
    bindMbandTabs(scope);
    bindBentoJump(scope);
    bindGallery(scope);
    bindPageTabs(scope);
  }

  bindSection(document);
  document.addEventListener('shopify:section:load', function (e) {
    bindSection(e.target);
    measureAnno();
  });

  /* --------------------- Announcement bar height ------------------------
     The bar sits above the header in normal flow, but the homepage header is
     absolutely positioned over the hero and anchored to the document, so it
     stays at y=0 and lands on the bar unless it is told how far to drop.
     Measuring beats a fixed height because a longer message wraps to two lines
     on a phone, and the header would then cover half of it. */
  function measureAnno() {
    var bar = document.querySelector('[data-anno]');
    var h = bar && bar.offsetParent !== null ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--anno-h', h + 'px');
  }
  measureAnno();
  window.addEventListener('resize', measureAnno, { passive: true });
  /* Webfonts land after this file runs and can change the line count. */
  window.addEventListener('load', measureAnno);

  /* ----------------------- Sticky header height -------------------------
     Anything else that wants to stick has to stop below the header, and the
     header's height is not a constant: it changes with the breakpoint and it
     shrinks when the overlay variant snaps to fixed. Published as a custom
     property so the stylesheet can say `top: var(--nav-h)` and stay honest. */
  function measureNav() {
    var nav = document.querySelector('.nav--sticky');
    var h = nav ? Math.round(nav.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--nav-h', h + 'px');
  }
  measureNav();
  window.addEventListener('resize', measureNav, { passive: true });
  window.addEventListener('load', measureNav);


  /* ------------------- Campaign tags → Klaviyo profile -------------------
     Visitors land on an ad URL like
       /pages/elahring-pre-launch?utm_source=meta&utm_campaign=806_dep_5
     but usually browse before they submit, so reading location.search at
     submit time finds nothing. Every page load stashes whatever tags it sees;
     the submit handler reads them back out.

     Both touches are kept. The unprefixed fields report the last one, because
     that is the visit the signup happened on; first_touch_* and last_touch_*
     carry the pair explicitly for anyone who needs to tell them apart. */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
                  'gclid', 'fbclid', 'ttclid'];
  var FIRST = 'elah:attr:first';
  var LAST = 'elah:attr:last';

  function store(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
  }
  function read(key) {
    try { return JSON.parse(window.localStorage.getItem(key)) || null; } catch (e) { return null; }
  }

  /* Which of the two stored visits a plain, unprefixed field reports: the last
     tagged one, falling back to the first for a key that visit did not carry.

     Shared because there are two reporters — the Klaviyo profile and the Meta
     lead event — and they have to agree. They did not: both preferred first
     touch, so somebody who arrived on ?utm_campaign=824, left, came back on
     ?utm_campaign=888 and signed up there was filed under 824 in both, and the
     campaign that actually converted looked like it had produced nothing. */
  function touch(first, last, key) { return last[key] || first[key] || ''; }

  /* Whatever tags the address bar is carrying right now. */
  function liveTags() {
    var q = new URLSearchParams(window.location.search);
    var hit = {};
    UTM_KEYS.forEach(function (k) {
      var v = q.get(k);
      if (v) hit[k] = v.slice(0, 200);
    });
    return hit;
  }

  function captureTags() {
    var hit = liveTags();
    if (!Object.keys(hit).length) return;
    hit.landing_page = window.location.pathname;
    hit.referrer = document.referrer || '';
    store(LAST, hit);
    if (!read(FIRST)) store(FIRST, hit);
  }
  captureTags();

  /* An in-app browser can hand back a page from its back-forward cache without
     re-running a line of script, and some open a second link inside the WebView
     that is already showing the first. Either way the capture above never runs
     for the new address, and the previous visit's tags stay behind — which is
     what an app reporting last week's campaign for today's signup looks like.
     pageshow fires on a restore where load does not. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) captureTags(); });

  function attribution() {
    var first = read(FIRST) || {};
    var last = read(LAST) || {};
    var body = document.body;
    var fallback = (body && body.dataset.klaviyoSource) || 'Website';

    /* The live address bar outranks both stored visits. toKlaviyo fires on the
       submit event, before the form navigates, so at that moment the URL still
       holds the tags of the visit that actually converted — and a value read
       from the URL cannot have gone stale in storage. Storage remains the
       fallback for the case it exists to cover: someone who arrived on a
       tagged link, browsed to an untagged page, and signed up there. */
    var now = liveTags();
    function of(key) { return now[key] || touch(first, last, key); }
    var src = of('utm_source');
    var camp = of('utm_campaign');

    var props = {
      /* Written twice on purpose: $source is Klaviyo's built-in field and can
         be overwritten by other integrations, so the plain copies are what
         segments should be built on. */
      '$source': src || fallback,
      source: src || fallback,
      campaign: camp,
      utm_source: src,
      utm_campaign: camp,
      utm_medium: of('utm_medium'),
      utm_content: of('utm_content'),
      utm_term: of('utm_term'),
      first_touch_source: first.utm_source || '',
      first_touch_campaign: first.utm_campaign || '',
      last_touch_source: last.utm_source || '',
      last_touch_campaign: last.utm_campaign || '',
      /* These two stay first touch whatever the plain fields do: they answer
         "how did this person first find us", which is a different question
         from "which ad closed it" and has its own answer. */
      landing_page: first.landing_page || '',
      referrer: first.referrer || '',
      signup_page: window.location.pathname
    };
    Object.keys(props).forEach(function (k) { if (props[k] === '') delete props[k]; });
    return props;
  }

  function toKlaviyo(email) {
    var body = document.body;
    var company = body && body.dataset.klaviyoCompany;
    var list = body && body.dataset.klaviyoList;
    var props = attribution();

    /* identify() is what actually writes source / campaign onto the profile,
       and it needs no list id — so it runs whether or not one is configured.
       It also ties the properties to the onsite cookie, so browsing recorded
       before the signup lands on the same profile. */
    if (window.klaviyo && window.klaviyo.identify) {
      try {
        var payload = { $email: email };
        Object.keys(props).forEach(function (k) { payload[k] = props[k]; });
        window.klaviyo.identify(payload);
      } catch (e) { /* noop */ }
    }

    /* The list subscription is the optional half: it records consent and puts
       them on a list. Skipped until a List ID is filled in. */
    if (!company || !list) return Promise.resolve();

    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      keepalive: true,            // survives the form's page navigation
      headers: { 'content-type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: props.source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, properties: props }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      })
    }).catch(function () { /* never block the signup on this */ });
  }

  /* Fire and forget on submit. The default submit is deliberately NOT
     prevented: the store has Shopify bot protection on, and its CAPTCHA token
     is injected during the native submit flow. Calling form.submit() from JS
     skips the submit event entirely, so the token never gets added and the
     post dies with "Missing CAPTCHA token". The keepalive flag on the Klaviyo
     request is what lets it finish after the page has navigated away. */
  document.querySelectorAll('form[action*="/contact"]').forEach(function (form) {
    form.addEventListener('submit', function () {
      if (form.dataset.klSent) return;
      var field = form.querySelector('input[type="email"]');
      var email = field && field.value && field.value.trim();
      if (!email) return;
      form.dataset.klSent = '1';
      toKlaviyo(email);
    });

    /* Bot protection eats the first submit. Its script cancels the event so it
       can load the captcha provider, and does not re-send the form afterwards
       — so the visitor clicks, nothing happens, and they conclude it is
       broken. Two things fix that: say something immediately, and send the
       form again once the provider has had a moment.

       The retry is self-limiting. If the first submit did go through the page
       is already navigating and this timer dies with it; it only ever fires in
       the case where nothing happened. */
    var btn = form.querySelector('button[type="submit"]');
    form.addEventListener('submit', function () {
      if (btn) { btn.classList.add('is-working'); btn.setAttribute('aria-busy', 'true'); }
      if (form.dataset.retryQueued) return;
      form.dataset.retryQueued = '1';
      window.setTimeout(function () {
        if (!document.body.contains(form)) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      }, 1400);
    });
  });

  /* ---------------------------- Sticky header ---------------------------
     Only the overlay header needs script. The solid one is in flow and does
     the whole job with position:sticky; this one is absolutely positioned
     over the hero, which sticky cannot act on, so it is switched to fixed
     once the original has scrolled out of the way. */
  (function () {
    var nav = document.getElementById('nav');
    if (!nav || !nav.classList.contains('nav--sticky')) return;
    if (!nav.classList.contains('nav--overlay')) return;

    /* Measured before anything sticks — the stuck bar is shorter, so reading
       it later would keep lowering its own trigger point. offsetTop is the
       announcement bar's height when there is one: the header does not begin
       at the top of the page then, and sticking it while its own original is
       still on screen shows the bar twice. */
    var trigger = (nav.offsetTop || 0) + (nav.offsetHeight || 64);
    var queued = false;

    function apply() {
      queued = false;
      var stuck = (window.pageYOffset || document.documentElement.scrollTop) > trigger;
      if (stuck === nav.classList.contains('is-stuck')) return;
      nav.classList.toggle('is-stuck', stuck);
    }

    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(apply);
    }, { passive: true });

    window.addEventListener('resize', function () {
      if (!nav.classList.contains('is-stuck')) trigger = (nav.offsetTop || 0) + (nav.offsetHeight || 64);
    }, { passive: true });

    apply();   /* deep-link or reload part-way down the page */
  })();

  /* ---------------------- Lead conversion event -------------------------
     The signup form is Shopify's own customer form: it POSTs the whole page
     and comes back to ?customer_posted=true. So the event fires from the
     RESULT, not from a submit handler — firing on submit would race the
     navigation and would also count attempts that failed validation.

     fbq() and gtag() are deliberately not called here because they do not
     exist in this context: the Meta pixel and GTM both run inside Shopify's
     web-pixel sandbox iframe. Shopify.analytics.publish is the supported
     bridge across it; a custom pixel subscribes to "lead_submitted" on the
     other side and forwards it to Meta as a standard Lead event. */
  (function () {
    /* Not location.search. The form's action carries a #contact_form anchor, and
       Shopify appends its parameter after it often enough that the address comes
       back as ...#contact_form?customer_posted=true — where the whole thing is
       the hash and search is empty. Every one of those submissions returned here
       and sent nothing, which is where the missing half of the Leads went.
       posted_successfully? is the server's own verdict and is stamped into the
       markup by the form snippet, so it is true exactly when the signup worked. */
    var q = new URLSearchParams(window.location.search);
    var posted = !!document.querySelector('[data-signup-ok]') ||
                 q.get('customer_posted') === 'true' ||
                 /[?&]customer_posted=true/.test(window.location.hash);
    if (!posted) return;

    /* Deduped per history entry, not per session. A fresh submission creates a
       new entry and must count — submitting a second address in the same tab is
       a second conversion, and during testing it is every attempt after the
       first. Only a back-navigation lands on an entry already counted. The
       session-wide flag this replaces also burned itself before the pixel check
       below, so any visit made while no Pixel ID was set silently disarmed
       every later submission in that tab. */
    var histState = (window.history && window.history.state) || {};
    if (histState.elahLead) return;

    var first = read(FIRST) || {};
    var last = read(LAST) || {};

    /* Same rule as the Klaviyo profile, deliberately: one signup must not be
       credited to two different campaigns depending on which report is read.
       The URL is checked first here too, though by this point the form has
       navigated and usually stripped it — which is exactly why the stored
       visits still have to be right. */
    var now = liveTags();
    function of(key) { return now[key] || touch(first, last, key); }
    var payload = {
      source: of('utm_source'),
      campaign: of('utm_campaign'),
      medium: of('utm_medium'),
      content: of('utm_content'),
      page: window.location.pathname
    };

    function send() {
      var S = window.Shopify;
      if (!S || !S.analytics || typeof S.analytics.publish !== 'function') return false;
      S.analytics.publish('lead_submitted', payload);
      return true;
    }
    /* Shopify bootstraps its analytics object asynchronously, and this file is
       deferred — measured on the live page, publish() is usually still missing
       at this point, so a plain existence check silently drops the conversion.
       Poll for it instead, then give up rather than leak a timer. */
    if (!send()) {
      var tries = 0;
      var wait = window.setInterval(function () {
        if (send() || ++tries > 100) window.clearInterval(wait);
      }, 100);
    }

    /* Harmless today (nothing reads it), but the moment a container is added
       in the page context rather than the sandbox, the event is already there. */
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'generate_lead',
      lead_source: payload.source,
      lead_campaign: payload.campaign,
      lead_page: payload.page
    });

    /* ---- Meta Lead, straight from the page ----
       The publish() above only reaches Meta if a custom pixel subscribes to it
       in Settings → Customer events. Where that has not been set up, filling in
       a Pixel ID in theme settings sends the Lead from here instead.

       This block only ever runs on the page a visitor lands on right after
       submitting, so fbevents.js (~110 KB) is downloaded by converters and by
       nobody else. Only 'Lead' is tracked — no PageView, because the Meta
       sales-channel app's own pixel already sends that and a second one would
       double the count. */
    var body = document.body;
    var pixelId = body && body.dataset.metaPixel;
    if (!pixelId) return;

    /* The sandboxed pixel is governed by Shopify's consent API; one loaded
       from the theme is not, so it has to check for itself. Absent API means
       the shop has no consent banner, which is the common case. */
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.marketingAllowed === 'function' && !privacy.marketingAllowed()) return;

    /* fbevents mints the _fbc click id by reading fbclid out of the URL as it
       initialises — but it only ever initialises HERE, on the post-submit URL,
       which no longer carries fbclid. Normally the Meta sales-channel app's
       pixel already minted it back on the landing page; where it did not (it
       is gated on the shop's consent settings, and skips whole regions), the
       conversion would reach Meta with no click id and could not be joined to
       an ad. The fbclid was captured at landing, so mint the cookie from it —
       only when absent, so a real one is never overwritten. */
    if (!/(^|;\s*)_fbc=/.test(document.cookie)) {
      var clickId = last.fbclid || first.fbclid;
      if (clickId) {
        document.cookie = '_fbc=fb.1.' + Date.now() + '.' + clickId +
          ';path=/;max-age=7776000;SameSite=Lax';
      }
    }

    if (typeof window.fbq !== 'function') {
      var n = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(s);
      window.fbq('init', pixelId);
    }
    var leadProps = {
      content_name: (body && body.dataset.metaLeadName) || 'Email signup',
      source: payload.source,
      campaign: payload.campaign
    };
    window.fbq('track', 'Lead', leadProps);

    /* Meta treats a standard event and a same-spelled custom one as unrelated,
       AND custom names are case-sensitive. The headless storefront on
       www.elahring.com fires trackMetaCustomEvent("Leads") and no standard Lead
       at all, so the account's existing Leads column is a custom conversion on
       that exact capitalised string — "Lead" and "leads" both score zero in it.
       The setting therefore takes a comma-separated list, so the theme matches
       whatever the ad sets were built against without another round trip.
       Separate names cannot double-count: a custom conversion rule matches one
       event name. */
    var customEvents = (body && body.dataset.metaCustomEvent) || '';
    customEvents.split(',').forEach(function (name) {
      var n = name.trim();
      if (n) window.fbq('trackCustom', n, leadProps);
    });

    /* Stamped only now, so an entry that sent nothing stays eligible. */
    try {
      var stamped = {};
      for (var k in histState) { if (Object.prototype.hasOwnProperty.call(histState, k)) stamped[k] = histState[k]; }
      stamped.elahLead = 1;
      window.history.replaceState(stamped, '');
    } catch (e) { /* history unavailable — worst case one extra Lead on back-nav */ }
  })();

  /* --------------------------- Waitlist popup ---------------------------
     Shopify's customer form posts the page and returns to
     ?customer_posted=true, so the popup is opened from that flag rather than
     from an AJAX response. ?popup=1 forces it open for design work. */
  (function () {
    var wl = document.querySelector('[data-waitlist]');
    if (!wl) return;

    var card = wl.querySelector('.wl__card');
    var lastFocus = null;

    function focusables() {
      return Array.prototype.slice.call(
        card.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
    }

    function open() {
      if (!wl.hasAttribute('hidden')) return;
      lastFocus = document.activeElement;
      wl.removeAttribute('hidden');
      document.documentElement.style.overflow = 'hidden';
      var f = focusables();
      (f[f.length - 1] || card).focus({ preventScroll: true });
    }

    function close() {
      if (wl.hasAttribute('hidden')) return;
      wl.setAttribute('hidden', '');
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    wl.querySelectorAll('[data-wl-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    /* Anything on the page marked data-wl-open opens it. Delegated from the
       document rather than bound to the triggers themselves: this runs once at
       load, while the theme editor replaces a section's markup on every
       settings change — a trigger bound directly would go dead the first time
       the merchant edited the band it sits in. */
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-wl-open]') : null;
      if (!t) return;
      e.preventDefault();
      open();
    });

    document.addEventListener('keydown', function (e) {
      if (wl.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      /* Trap focus inside the dialog. */
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* Two ways in. The flag is the reliable one — Liquid stamps it from
       form.posted_successfully?, which is the server's own verdict on the
       signup. The query parameter is kept because Shopify does send it on some
       paths, and ?popup=1 is there to open the thing for design work. */
    var q = new URLSearchParams(window.location.search);
    var posted = !!document.querySelector('[data-signup-ok]') ||
                 q.get('customer_posted') === 'true' ||
                 /[?&]customer_posted=true/.test(window.location.hash);
    if (posted || q.get('popup') === '1') {
      open();
      /* Drop the flag so a refresh or a shared link does not re-open it. */
      if (q.get('customer_posted') === 'true' && window.history.replaceState) {
        q.delete('customer_posted');
        var s = q.toString();
        window.history.replaceState({}, '', window.location.pathname + (s ? '?' + s : ''));
      }
    }
  })();

  /* -------------------------- Ring size form ----------------------------
     Reporting the size the sizing kit gave, after the order was placed.

     Every request goes to the endpoint named in the section settings, never to
     Shopify: the token that can rewrite an order has to stay on a server. The
     endpoint answers the lookup with the rings on that order, and this only
     draws them and posts back what was chosen. */
  function bindRingSizeForm(scope) {
    find(scope, '[data-ring-size-form]').forEach(function (root) {
      var endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
      var sizes = (root.dataset.sizes || '').split(',').map(function (s) { return s.trim(); })
                    .filter(function (s) { return s; });
      var strings = {};
      try {
        strings = JSON.parse(root.querySelector('[data-rsize-strings]').textContent);
      } catch (e) { /* schema defaults cover every key, so this cannot lose copy */ }

      var lookup = root.querySelector('[data-step="lookup"]');
      var sizesForm = root.querySelector('[data-step="sizes"]');
      var done = root.querySelector('[data-step="done"]');
      var ringsBox = root.querySelector('[data-rings]');
      var foundNote = root.querySelector('[data-found]');
      var lookupErr = root.querySelector('[data-lookup-error]');
      var submitErr = root.querySelector('[data-submit-error]');
      var back = root.querySelector('[data-back]');
      if (!lookup || !sizesForm || !ringsBox) return;

      var order = null;
      var busy = false;

      function show(step) {
        lookup.hidden = step !== 'lookup';
        sizesForm.hidden = step !== 'sizes';
        if (done) done.hidden = step !== 'done';
      }

      function fail(el, msg) {
        if (!el) return;
        el.textContent = msg;
        el.hidden = false;
      }

      function fill(tpl, map) {
        return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) {
          return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m;
        });
      }

      function working(form, on) {
        var btn = form.querySelector('button[type="submit"]');
        if (!btn) return;
        btn.classList.toggle('is-working', on);
        if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
      }

      /* One block per ring. A ring that already carries a size is shown but
         locked: seeing the number that was recorded is what stops a customer
         submitting a second time "just in case", and re-opening it would let a
         mistake overwrite something already in production. */
      function draw(rings) {
        ringsBox.textContent = '';
        rings.forEach(function (ring, i) {
          var wrap = document.createElement('fieldset');
          wrap.className = 'rsize__ring';
          wrap.dataset.ringId = ring.id || '';

          var legend = document.createElement('legend');
          legend.className = 'rsize__ring-title';
          legend.textContent = fill(strings.ringLabel, { n: i + 1 }) +
                               (ring.variant ? ' · ' + ring.variant : '');
          wrap.appendChild(legend);

          if (ring.size) {
            var had = document.createElement('p');
            had.className = 'rsize__ring-done';
            had.textContent = fill(strings.alreadyLabel, { size: ring.size });
            wrap.appendChild(had);
          } else {
            var row = document.createElement('div');
            row.className = 'sizes';
            sizes.forEach(function (s) {
              var label = document.createElement('label');
              label.className = 'sizedot';
              var input = document.createElement('input');
              input.type = 'radio';
              input.name = 'size-' + i;
              input.value = s;
              input.setAttribute('data-ring-size', '');
              var num = document.createElement('span');
              num.className = 'sizedot__num';
              /* The stored value keeps the product's own "#10"; the circle
                 shows the number, because a hash inside a 48px circle is two
                 thirds decoration. */
              num.textContent = s.replace(/^#/, '');
              label.appendChild(input);
              label.appendChild(num);
              row.appendChild(label);
            });
            wrap.appendChild(row);
          }
          ringsBox.appendChild(wrap);
        });
      }

      lookup.addEventListener('submit', function (e) {
        e.preventDefault();
        if (busy) return;
        if (lookupErr) lookupErr.hidden = true;
        if (!endpoint) { fail(lookupErr, strings.unconfigured); return; }

        var number = lookup.querySelector('[name="order"]').value.trim();
        var email = lookup.querySelector('[name="email"]').value.trim();
        if (!number || !email) { fail(lookupErr, strings.fields || strings.missing); return; }

        busy = true; working(lookup, true);
        fetch(endpoint + '/lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order: number, email: email })
        }).then(function (r) { return r.json().catch(function () { return {}; }); })
          .then(function (data) {
            if (!data || !data.ok || !data.rings || !data.rings.length) {
              fail(lookupErr, strings.notFound);
              return;
            }
            order = { number: number, email: email, token: data.token || '' };
            draw(data.rings);
            if (foundNote) {
              foundNote.textContent = fill(strings.found, { order: number, n: data.rings.length });
            }
            show('sizes');
          })
          .catch(function () { fail(lookupErr, strings.network); })
          .then(function () { busy = false; working(lookup, false); });
      });

      if (back) {
        back.addEventListener('click', function () {
          order = null;
          if (submitErr) submitErr.hidden = true;
          show('lookup');
        });
      }

      sizesForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (busy || !order) return;
        if (submitErr) submitErr.hidden = true;

        var payload = [];
        var incomplete = false;
        ringsBox.querySelectorAll('.rsize__ring').forEach(function (block) {
          /* Already-sized rings carry no inputs and are not resubmitted. */
          if (!block.querySelector('[data-ring-size]')) return;
          var on = block.querySelector('[data-ring-size]:checked');
          if (!on) { incomplete = true; return; }
          payload.push({ id: block.dataset.ringId, size: on.value });
        });
        if (incomplete) { fail(submitErr, strings.missing); return; }
        if (!payload.length) { show('done'); return; }

        busy = true; working(sizesForm, true);
        fetch(endpoint + '/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order: order.number, email: order.email, token: order.token, sizes: payload
          })
        }).then(function (r) { return r.json().catch(function () { return {}; }); })
          .then(function (data) {
            if (!data || !data.ok) { fail(submitErr, strings.network); return; }
            show('done');
          })
          .catch(function () { fail(submitErr, strings.network); })
          .then(function () { busy = false; working(sizesForm, false); });
      });

      show('lookup');
    });
  }

  /* ----------------------------- Page tabs ------------------------------
     Clicking a tab goes to its section; scrolling past a section marks its
     tab. Sections are found by the tail of their id because Shopify stamps
     the template's own number on the front of every one, and a full id
     written down here would stop matching the moment the page is duplicated. */
  function bindPageTabs(scope) {
    find(scope, '[data-page-tabs]').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab]'));
      if (!tabs.length) return;

      var pairs = tabs.map(function (tab) {
        var name = tab.dataset.tab;
        var target = null;
        if (name) {
          target = document.querySelector('[id$="__' + name + '"]') ||
                   document.getElementById('shopify-section-' + name);
          /* A section the merchant adds in the editor gets a random suffix —
             community_gDKGPi, not community — so neither lookup above finds it
             and the tab silently does nothing when pressed. Fall back to the
             first section whose id merely contains the name. Last resort, and
             only after both exact forms have missed. */
          if (!target) {
            target = document.querySelector('[id^="shopify-section-"][id*="' + name + '"]');
          }
        }
        return { tab: tab, target: target };
      });

      function mark(tab) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-current', String(on));
        });
        /* The bar scrolls sideways on a phone; keep the marked tab in it. */
        var bar = root.querySelector('.ptabs__bar');
        if (bar && bar.scrollWidth > bar.clientWidth + 1) {
          bar.scrollTo({ left: Math.max(0, tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2), behavior: 'smooth' });
        }
      }

      pairs.forEach(function (p) {
        p.tab.addEventListener('click', function () {
          mark(p.tab);
          if (!p.target) return;
          /* Land below the bar rather than under it. */
          var offset = root.getBoundingClientRect().height;
          var y = window.pageYOffset + p.target.getBoundingClientRect().top - offset;
          scrollPageTo(y);
        });
      });

      /* Whichever section's top has most recently passed the bar is the one
         being read. Measured out of the scroll handler rather than deferred a
         frame: a handful of rects, and the deferred version only holds while
         frames keep coming. */
      var found = pairs.filter(function (p) { return p.target; });
      if (!found.length) return;
      window.addEventListener('scroll', function () {
        var line = root.getBoundingClientRect().bottom + 8;
        var current = found[0];
        found.forEach(function (p) {
          if (p.target.getBoundingClientRect().top <= line) current = p;
        });
        if (!current.tab.classList.contains('is-on')) mark(current.tab);
      }, { passive: true });
    });
  }

  /* ----------------------------- Fine print -----------------------------
     Clips the disclaimer block and reveals the button that lifts the clip.
     Both are done here rather than in the markup so that a page whose script
     never runs shows the text in full: unreadable-because-long beats
     unreadable-because-cut-off-with-no-way-to-open-it. */
  /* ------------------------- Media band tabs ----------------------------
     A row of pills that swaps the panel beside the copy and the copy itself.
     Every pane is already in the markup; this only moves the `hidden`
     attribute, so the pictures are decoded before the first press rather than
     fetched on it. */
  /* ------------------------- Bento card jumps ---------------------------
     A card that names a section further down scrolls to it. Matching the tail
     of the id rather than a written-out anchor, because Shopify prefixes every
     section id with the template's own number. */
  /* A smooth scroll that always arrives. Chrome drops a programmatic smooth
     scroll outright in contexts where no frames are being produced — a
     background tab, some in-app webviews, a machine with animations off at the
     OS level — and the page then simply never moves. Nothing reports an error;
     it just sits there. So: ask for smooth, and if nothing has budged shortly
     after, jump. Arriving abruptly beats not arriving. */
  function scrollPageTo(y) {
    y = Math.max(0, Math.round(y));
    var from = Math.round(window.pageYOffset);
    if (Math.abs(y - from) < 2) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, y);
      return;
    }
    /* Two checks, because a smooth scroll fails in two different ways. It can
       be dropped outright — nothing moves at all — and it can start and then
       stall, leaving the reader somewhere in the middle of the page with no
       idea what the press did. The first check catches the drop, the second
       catches the stall.

       Both stand down if the reader has touched the page: Chrome cancels a
       programmatic smooth scroll on any real input, and correcting after that
       would be yanking the page out from under them. */
    var touched = false;
    function noteInput() { touched = true; }
    var opts = { passive: true };
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (e) {
      window.addEventListener(e, noteInput, opts);
    });
    function done() {
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (e) {
        window.removeEventListener(e, noteInput, opts);
      });
    }
    window.scrollTo({ top: y, behavior: 'smooth' });
    setTimeout(function () {
      if (touched) { done(); return; }
      if (Math.abs(Math.round(window.pageYOffset) - from) < 2) window.scrollTo(0, y);
    }, 280);
    setTimeout(function () {
      if (!touched && Math.abs(Math.round(window.pageYOffset) - y) > 4) window.scrollTo(0, y);
      done();
    }, 900);
  }

  /* Same guarantee for a scroller that is not the page — the gallery track.
     A dropped smooth scroll there leaves the arrows and dots looking dead. */
  function scrollTrackTo(el, left) {
    left = Math.max(0, Math.round(left));
    var from = Math.round(el.scrollLeft);
    if (Math.abs(left - from) < 2) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.scrollLeft = left;
      return;
    }
    el.scrollTo({ left: left, behavior: 'smooth' });
    setTimeout(function () {
      if (Math.abs(Math.round(el.scrollLeft) - from) < 2) el.scrollLeft = left;
    }, 280);
  }

  /* -------------------------- Product gallery ---------------------------
     The track is the single source of truth for which picture is showing: the
     buttons scroll it and the scroll position decides the dots. Driving a
     separate index would let a swipe and a button press disagree. */
  function bindGallery(scope) {
    find(scope, '[data-gallery]').forEach(function (root) {
      var track = root.querySelector('[data-gallery-track]');
      if (!track) return;
      var bar   = root.querySelector('[data-gallery-bar]');
      var all   = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
      var allDots = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-dot]'));
      var prev = root.querySelector('[data-gallery-prev]');
      var next = root.querySelector('[data-gallery-next]');
      if (all.length < 2) return;

      /* The showing subset. Picking a finish narrows it; everything below
         counts against these two lists rather than the full set, so the dots
         and the arrows agree with what is actually on the track. */
      var slides = all;
      var dots = allDots;

      function index() {
        var span = track.scrollWidth - track.clientWidth;
        if (span < 1) return 0;
        return Math.round(track.scrollLeft / span * (slides.length - 1));
      }

      /* Takes the index when the caller already knows it. Pressing an arrow
         moved the dots only once the scroll event arrived, which makes the
         control feel unresponsive on a slow scroll and leaves it stuck
         entirely anywhere those events are throttled. Paint on the press,
         then again from the real position once the track settles. */
      /* Give a slide its picture. Everything past the first ships with its
         source on data- attributes, because a horizontal scroller defeats
         loading="lazy" — the browser sees every slide as level with the
         viewport and fetches the lot at once. This hands them out instead:
         the one being looked at and its two neighbours, so a press of an arrow
         has the next picture already there rather than starting to fetch it. */
      function hydrate(n) {
        var slide = slides[n];
        if (!slide) return;
        var img = slide.querySelector('img[data-src]');
        if (!img) return;
        var ss = img.getAttribute('data-srcset');
        if (ss) { img.setAttribute('srcset', ss); img.removeAttribute('data-srcset'); }
        img.setAttribute('src', img.getAttribute('data-src'));
        img.removeAttribute('data-src');
      }
      function hydrateAround(i) {
        hydrate(i); hydrate(i - 1); hydrate(i + 1);
      }
      /* Anything still holding a data-src, all at once. Used when the reader
         has clearly settled in — they have dragged or paged — at which point
         guarding the bytes matters less than never making them wait again. */
      function hydrateAll() {
        for (var n = 0; n < slides.length; n++) hydrate(n);
      }

      function paint(i) {
        if (i == null) i = index();
        hydrateAround(i);
        allDots.forEach(function (d) { d.classList.remove('is-on'); d.setAttribute('aria-selected', 'false'); });
        if (dots[i]) { dots[i].classList.add('is-on'); dots[i].setAttribute('aria-selected', 'true'); }
        if (prev) prev.disabled = i <= 0;
        if (next) next.disabled = i >= slides.length - 1;
      }

      function go(i, smooth) {
        var n = Math.max(0, Math.min(slides.length - 1, i));
        if (!slides[n]) return;
        var left = slides[n].offsetLeft - track.offsetLeft;
        /* Filtering to a finish jumps rather than glides: the track is being
           rebuilt underneath, and animating to a slide that just appeared
           reads as a glitch rather than a transition. */
        if (smooth === false) track.scrollLeft = Math.max(0, Math.round(left));
        else scrollTrackTo(track, left);
        paint(n);
      }

      track.addEventListener('scroll', function () { paint(); }, { passive: true });
      window.addEventListener('resize', function () { paint(); }, { passive: true });
      if (prev) prev.addEventListener('click', function () { go(index() - 1); });
      if (next) next.addEventListener('click', function () { go(index() + 1); });
      allDots.forEach(function (d) {
        d.addEventListener('click', function () { hydrateAll(); go(dots.indexOf(d)); });
      });
      if (prev) prev.addEventListener('click', hydrateAll);
      if (next) next.addEventListener('click', hydrateAll);
      track.addEventListener('pointerdown', hydrateAll, { once: true });
      track.addEventListener('touchstart', hydrateAll, { once: true, passive: true });

      /* Choosing a finish shows that finish and nothing else. A picture is
         filed under a finish by its alt text — see the section for the #tag —
         and a picture with no tag is shared, so it stays whatever is chosen.

         If a finish has no pictures of its own the track is left whole rather
         than emptied: a half-filled media library should still show the ring. */
      function applyColour(want) {
        var owned = 0;
        all.forEach(function (s) {
          if ((s.getAttribute('data-slide-colour') || '') === want) owned++;
        });

        /* Filtering off: the carousel stays whole and choosing a finish moves
           to that finish's first picture instead. Hiding nine of twelve makes
           the gallery look thinner than the library actually is, which is the
           opposite of what a product page wants — so the section can now be
           told to leave the pictures alone and only navigate. */
        if (root.hasAttribute('data-gallery-nofilter')) {
          all.forEach(function (s, n) {
            s.hidden = false;
            if (allDots[n]) allDots[n].hidden = false;
          });
          slides = all;
          dots = allDots;
          if (bar) bar.hidden = all.length < 2;
          /* go() paints with the index it was given. Painting again here
             would recompute it from a scroll that has only just been asked to
             move, so the dots would show where the track was rather than where
             it is going — and the correction never came, because the position
             is already final by the time the scroll settles. */
          var target = -1;
          if (want) {
            for (var i = 0; i < all.length; i++) {
              if ((all[i].getAttribute('data-slide-colour') || '') === want) { target = i; break; }
            }
          }
          if (target >= 0) go(target); else paint();
          return;
        }

        if (!want || !owned) {
          all.forEach(function (s, n) {
            s.hidden = false;
            if (allDots[n]) allDots[n].hidden = false;
          });
          slides = all;
          dots = allDots;
          if (bar) bar.hidden = false;
          /* Nothing is tagged for this finish, so fall back to the older
             behaviour: page to the first picture whose alt merely mentions it. */
          if (want) {
            for (var n = 0; n < all.length; n++) {
              var img = all[n].querySelector('img');
              var alt = ((img && img.getAttribute('alt')) || '')
                .toLowerCase().replace(/[\s\-_]/g, '');
              if (alt.indexOf(want) !== -1) { go(n); break; }
            }
          }
          paint();
          return;
        }

        var shown = [], shownDots = [];
        all.forEach(function (s, n) {
          var key = s.getAttribute('data-slide-colour') || '';
          var on = key === '' || key === want;
          s.hidden = !on;
          if (allDots[n]) allDots[n].hidden = !on;
          if (on) { shown.push(s); if (allDots[n]) shownDots.push(allDots[n]); }
        });
        slides = shown;
        dots = shownDots;
        if (bar) bar.hidden = shown.length < 2;
        go(0, false);
        paint();
        hydrateAround(0);
      }

      function keyOf(radio) {
        if (!radio) return '';
        return (radio.getAttribute('data-colour-slug') ||
                (radio.value || '').toLowerCase().replace(/[\s\-_]/g, '')).trim();
      }

      /* Drag to page through it. A phone already swipes — the track is a real
         scroller — but a mouse cannot, so on a desktop the pictures could only
         be changed from the small controls underneath. Pointer events cover
         mouse, pen and touch; touch is left to the browser's own scrolling,
         which has momentum and rubber-banding this could only imitate badly. */
      var drag = null;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch' || e.button !== 0) return;
        drag = { x: e.clientX, left: track.scrollLeft, moved: false, from: index(), dx: 0 };
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) < 4) return;   // a click, not a drag
        drag.moved = true;
        drag.dx = dx;
        /* scroll-snap fights a scrollLeft written mid-gesture, pulling the
           track back to the nearest slide on every frame. Suspended while the
           pointer is down and restored on release, which is also when the snap
           should happen. */
        track.style.scrollSnapType = 'none';
        track.scrollLeft = drag.left - dx;
        e.preventDefault();
      });
      function endDrag() {
        if (!drag) return;
        var moved = drag.moved, dx = drag.dx, from = drag.from;
        drag = null;
        track.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
        if (!moved) return;
        /* Settling on whichever slide is nearest means a flick shorter than
           half a picture springs back, which reads as the drag having failed.
           A fifth of the width is enough to mean "next" — the same forgiveness
           a native swipe gives. Anything shorter returns where it started. */
        var step = slides[0] ? slides[0].getBoundingClientRect().width : track.clientWidth;
        if (Math.abs(dx) > step * 0.2) go(from + (dx < 0 ? 1 : -1));
        else go(from);
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('pointerleave', endDrag);
      /* A drag that ends over a picture must not also count as a click on it. */
      track.addEventListener('click', function (e) {
        if (track.classList.contains('was-dragged')) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });

      var panel = root.closest('.ringpick__grid') || document;
      panel.addEventListener('change', function (e) {
        var r = e.target;
        if (!r || !r.hasAttribute || !r.hasAttribute('data-colour')) return;
        applyColour(keyOf(r));
      });

      /* ---------------------------- Autoplay ----------------------------
         Off unless the section asks for it. Three rules, in order of how much
         they matter:

         1. Any deliberate move by the visitor stops it for good. Once someone
            is steering — an arrow, a dot, a drag — taking the wheel back and
            sliding the picture out from under them is the thing people
            complain about in carousels. This does not come back.
         2. Hovering or holding a finger down pauses it. That is someone
            looking, not steering, so it resumes when they leave.
         3. A hidden tab pauses it, and reduced-motion turns it off outright.

         The dot's fill doubles as the clock. The frame draws the current slide
         as a 44-long track with 30 of it filled, which only makes sense as a
         progress bar — so with a timer running it actually is one. */
      var apMs = parseInt(root.getAttribute('data-autoplay'), 10) || 0;
      var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (apMs > 0 && !still && slides.length > 1) {
        var timer = null, stopped = false, paused = false;
        root.style.setProperty('--gal-ms', apMs + 'ms');

        function arm() {
          /* Idempotent, deliberately. Assigning to `timer` replaces the handle
             but does not cancel what it pointed at, and arm() has several ways
             in — load, resume from a hover, a visibility change — so a second
             call used to leave the first timer running. Each one re-armed
             itself on fire, so they multiplied, and the gallery lurched back
             and forth between two or three pictures at a second or so apart
             instead of stepping through them every five. */
          window.clearTimeout(timer);
          if (stopped || paused || document.hidden) return;
          var live = dots[index()];
          if (live) { live.classList.remove('is-timing'); void live.offsetWidth; live.classList.add('is-timing'); }
          timer = window.setTimeout(function () {
            var next = index() + 1;
            go(next >= slides.length ? 0 : next);
            arm();
          }, apMs);
        }
        function disarm() {
          window.clearTimeout(timer);
          timer = null;
          allDots.forEach(function (d) { d.classList.remove('is-timing'); });
        }
        function stopForGood() { stopped = true; disarm(); }
        function pause() { if (stopped) return; paused = true; disarm(); }
        function resume() { if (stopped || !paused) return; paused = false; arm(); }

        [prev, next].forEach(function (b) { if (b) b.addEventListener('click', stopForGood); });
        allDots.forEach(function (d) { d.addEventListener('click', stopForGood); });
        track.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'touch') pause(); else stopForGood();
        });
        track.addEventListener('touchstart', pause, { passive: true });
        track.addEventListener('touchend', resume, { passive: true });
        root.addEventListener('mouseenter', pause);
        root.addEventListener('mouseleave', resume);
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) disarm(); else if (!stopped && !paused) arm();
        });
        /* Nothing starts until the page has finished loading — a timer that
           begins during the LCP window competes with it for the main thread. */
        if (document.readyState === 'complete') arm();
        else window.addEventListener('load', arm);
      }

      /* The first finish is already selected when the page arrives, so the
         gallery has to start narrowed rather than showing every finish at once
         until something is clicked. */
      var checked = panel.querySelector ? panel.querySelector('[data-colour]:checked') : null;
      if (checked) applyColour(keyOf(checked)); else paint();
    });
  }

  function bindBentoJump(scope) {
    find(scope, '[data-bento-jump]').forEach(function (card) {
      /* On the card, not on the title button. The button carries a stretched
         ::after meant to cover the card, but .bento__media paints over it, so
         only the few words of the title were ever clickable — everywhere that
         looks like the picture did nothing. Listening on the card makes the
         whole tile the target and cannot be undone by a later overlay. The
         title stays a real <button> so the card is still reachable by keyboard;
         its click bubbles up to here, so it needs no handler of its own. */
      card.addEventListener('click', function () {
        var want = card.getAttribute('data-bento-jump');
        var target = document.querySelector('[id$="__' + want + '"]') ||
                     document.getElementById(want);
        if (!target) return;
        /* Land under whatever is stuck to the top rather than behind it. */
        var stuck = document.querySelector('.ptabs--sticky');
        var head = document.querySelector('.nav--sticky.is-stuck, .nav--solid.nav--sticky');
        var off = (stuck ? stuck.offsetHeight : 0) + (head ? head.offsetHeight : 0);
        scrollPageTo(target.getBoundingClientRect().top + window.pageYOffset - off);
      });
    });
  }

  function bindMbandTabs(scope) {
    find(scope, '[data-mband-tabs]').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-mband-tab]'));
      if (!tabs.length) return;
      var panes = Array.prototype.slice.call(root.querySelectorAll('[data-mband-pane]'));
      var copies = Array.prototype.slice.call(root.querySelectorAll('[data-mband-copy]'));

      function show(i) {
        tabs.forEach(function (t, n) {
          var on = n === i;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        [panes, copies].forEach(function (group) {
          group.forEach(function (el, n) {
            var on = n === i;
            el.classList.toggle('is-on', on);
            if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
          });
        });
      }

      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { show(i); });
        /* Left/right move between tabs, which is what a tablist is expected to
           do and what the pills look like they should already do. */
        t.addEventListener('keydown', function (e) {
          var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          var n = (i + d + tabs.length) % tabs.length;
          tabs[n].focus();
          show(n);
        });
      });
    });
  }

  function bindFinePrint(scope) {
    find(scope, '[data-fineprint]').forEach(function (body) {
      var section = body.closest('.fineprint');
      var toggle = section && section.querySelector('[data-fineprint-toggle]');
      var label = toggle && toggle.querySelector('[data-fineprint-label]');
      if (!toggle) return;

      var more = toggle.dataset.more || (label && label.textContent) || 'Show more';
      var less = toggle.dataset.less || 'Show less';

      function fits() {
        /* Measured against the clip height the stylesheet would apply, not
           against the element's current height — which is the full height
           while it is still unclipped. */
        var clip = parseFloat(getComputedStyle(section).getPropertyValue('--fineprint-clip')) || 260;
        return body.scrollHeight <= clip + 4;
      }

      function apply() {
        if (fits()) {
          body.classList.remove('is-clipped');
          toggle.hidden = true;
          return;
        }
        toggle.hidden = false;
        if (toggle.getAttribute('aria-expanded') !== 'true') body.classList.add('is-clipped');
      }

      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        body.classList.toggle('is-clipped', open);
        if (label) label.textContent = open ? more : less;
      });

      apply();
      /* Webfonts land after this runs and change how many lines the text
         takes, which is exactly what decides whether the button is needed. */
      window.addEventListener('load', apply);
      window.addEventListener('resize', apply, { passive: true });
    });
  }

  /* ------------------------ Support form result -------------------------
     The contact form posts and comes back to the top of the same page, where
     on a phone the confirmation is a screen and a half below the fold. Someone
     who has just filled in a form and been returned to what looks like the
     same page needs to be shown the answer, not left to go looking for it. */
  function bindSupportDone(scope) {
    /* Marked at submit time and read on the way back. Neither of the two
       things the server could have told us survives the trip:

         - Without return_to, Shopify picks the destination itself and picked
           About Us — a support request answered with a page about the company.
         - With return_to it honours the address exactly and stops appending
           contact_posted=true, so the page comes back with nothing to read.
         - Putting the flag in return_to does not work either: a return_to
           carrying a query string is rejected outright and the visitor lands
           on the home page.

       So the page records that it submitted, and reads that back. sessionStorage
       rather than a variable because the form navigates; the path is stored with
       it so a submission on one page cannot announce itself on another. */
    var KEY = 'elah:support:sent';

    find(scope, '[data-support-done]').forEach(function (done) {
      var card = done.parentElement;
      var wrap = card && card.querySelector('[data-support-formwrap]');
      var form = wrap && wrap.querySelector('form');

      if (form) {
        form.addEventListener('submit', function () {
          try { window.sessionStorage.setItem(KEY, window.location.pathname); } catch (e) { /* private mode */ }
        });
      }

      var flagged = false;
      try {
        flagged = window.sessionStorage.getItem(KEY) === window.location.pathname;
        if (flagged) window.sessionStorage.removeItem(KEY);
      } catch (e) { /* private mode: the parameter below is the fallback */ }

      /* Kept as a second route in case Shopify ever supplies it, and for the
         private-browsing case where storage is unavailable. Both search and
         hash, because the form's action carries a #contact_form anchor and the
         parameter sometimes lands after it. */
      var url = window.location;
      var param = /[?&]contact_posted=true/.test(url.search) ||
                  /[?&]contact_posted=true/.test(url.hash);

      if (!flagged && !param && done.hidden) return;

      /* A submission Shopify rejected comes back down the same road as one it
         accepted. The errors it rendered are the difference. */
      if (wrap && wrap.querySelector('.support__error')) return;

      done.hidden = false;
      if (wrap) wrap.hidden = true;

      /* The redirect lands at the top of the page and on a phone this card is
         a screen and a half below the fold. Somebody who has just filled in a
         form and been returned to what looks like the same page has to be
         shown the answer, not left to go looking for it. focus() as well as
         the scroll, because it moves a screen reader's place too. */
      done.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { done.focus({ preventScroll: true }); } catch (e) { /* older browser */ }

      if (param && window.history && window.history.replaceState) {
        var q = new URLSearchParams(url.search);
        q.delete('contact_posted');
        var rest = q.toString();
        window.history.replaceState({}, '', url.pathname + (rest ? '?' + rest : ''));
      }
    });
  }

  /* --------------------------- Sticky buy bar ---------------------------
     Shows once the real buy panel has scrolled past, hides on the way back.
     Measured out of the scroll handler rather than deferred a frame: it is one
     rect, and the deferred version only holds while frames keep coming.

     The button scrolls back to the panel instead of adding to the cart. The
     colour and size live there, and a bar that could buy without them would
     either guess or fail. */
  function bindStickyBuy(scope) {
    find(scope, '[data-sticky-buy]').forEach(function (bar) {
      var panel = document.querySelector(bar.dataset.watch || '[data-ring-picker]');
      if (!panel) { bar.remove(); return; }

      var nameEl = bar.querySelector('[data-sticky-name]');
      var dots = bar.querySelector('[data-sticky-dots]');

      /* The dots mirror the panel's swatches — built from them so the two
         cannot list different colours. */
      if (dots) {
        var swatches = panel.querySelectorAll('[data-colour]');
        swatches.forEach(function (input) {
          var li = document.createElement('li');
          /* A real button, not a decorative disc. Pinned from the top the bar
             is often the only colour control on screen — the panel has scrolled
             away — so a dot you can see but not press is a dead control in the
             one place it matters. Pressing it drives the panel's own radio and
             lets the change event do the rest, so the price, the name, the
             gallery filter and these dots all follow one source. */
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'stickybuy__dot';
          btn.setAttribute('aria-label', input.value);
          btn.addEventListener('click', function () {
            if (input.checked) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          /* The change handler below finds dots by this attribute, and the
             dot is the button now — not the li that carries it. */
          btn.dataset.colour = input.value;
          li.appendChild(btn);
          /* The finish itself, not just its name. The panel's label carries the
             colour as --swatch; copied across, these dots say which finishes
             exist the same way the panel does. Without it they were four
             identical grey discs — a control that names four options and shows
             none of them. */
          var label = input.closest('.swatch');
          var hex = label ? getComputedStyle(label).getPropertyValue('--swatch').trim() : '';
          if (hex) btn.style.setProperty('--swatch', hex);
          if (input.checked) btn.classList.add('is-on');
          dots.appendChild(li);
        });
        /* The bar's thumbnail is the finish in miniature, so it has to move with
           the swatches — it was the one part of the bar that stayed on whatever
           the page opened with, which read as the picture being wrong rather
           than static. The gallery already holds one slide per finish, tagged
           with the same slug the swatch carries, so the picture is taken from
           there rather than fetched again. Slides past the first are lazy and
           keep their address in data-src until hydrated; both are checked. */
        var thumbEl = bar.querySelector('.stickybuy__thumb');
        function thumbFor(slug) {
          if (!slug) return '';
          var slide = panel.querySelector('[data-gallery-slide][data-slide-colour="' + slug + '"]');
          var im = slide && slide.querySelector('img');
          var u = im && (im.getAttribute('src') || im.getAttribute('data-src'));
          /* Asked for at the size it is drawn at, not the gallery's width. */
          return u ? u.replace(/([?&]width=)\d+/, '$1120') : '';
        }

        panel.addEventListener('change', function (e) {
          if (!e.target.hasAttribute || !e.target.hasAttribute('data-colour')) return;
          if (nameEl) nameEl.textContent = e.target.value;
          dots.querySelectorAll('.stickybuy__dot').forEach(function (d) {
            d.classList.toggle('is-on', d.dataset.colour === e.target.value);
          });
          if (thumbEl) {
            var u = thumbFor(e.target.getAttribute('data-colour-slug'));
            /* srcset would win over src and put the old picture straight back. */
            if (u) { thumbEl.removeAttribute('srcset'); thumbEl.removeAttribute('sizes'); thumbEl.src = u; }
          }
        });
      }

      var cta = bar.querySelector('[data-sticky-cta]');
      if (cta) {
        cta.addEventListener('click', function () {
          var action = cta.getAttribute('data-cta-action') || 'panel';
          /* The popup opens itself: the button carries data-wl-open and the
             delegated handler further down takes it from there, so there is
             nothing to do here but stay out of the way. */
          if (action === 'popup') return;
          if (action === 'link') {
            var href = cta.getAttribute('data-cta-link');
            if (href) window.location.href = href;
            return;
          }
          /* Checkout with whatever the panel is currently showing. A cart
             permalink takes a numeric variant id and nothing else — no handle,
             no option names — so typing one into the settings pins every
             visitor to that single colour and size. This reads the id the panel
             has already resolved from the swatch and the size, so the press
             carries the customer's own choice.

             If nothing is resolved yet — a colour whose size is sold out, or a
             size not picked — there is no id to send. Scroll to the panel
             instead of guessing one. */
          if (action === 'variant') {
            /* This used to jump to a cart permalink, /cart/<variant>:1. That is
               one click to checkout, which is the point of the button — but a
               permalink carries a variant id and nothing else, so the line item
               reached the order with no _ring_id, no size and no sizing route
               on it. Those properties are what the ring-size page matches on,
               so every order bought this way was unanswerable.

               Submitting the panel's own form instead keeps the one press and
               keeps the properties: the panel adds the ring (and the kit) with
               everything attached, then hands over to checkout. */
            var pRoot = document.querySelector('[data-ring-picker]');
            var pForm = pRoot && pRoot.querySelector('form');
            if (pRoot && pForm) {
              pRoot.dataset.afterAdd = 'checkout';
              if (typeof pForm.requestSubmit === 'function') {
                pForm.requestSubmit();
              } else {
                pForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }
            } else {
              panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
          /* Pinned from the top, the panel is often already on screen and
             scrolling to it would look like the button did nothing. Move only
             when it is actually away, and either way put the cursor on the real
             Add to Cart so the press always leads somewhere. */
          var r = panel.getBoundingClientRect();
          var vh = window.innerHeight || document.documentElement.clientHeight;
          if (r.bottom < 80 || r.top > vh - 80) {
            panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          var add = panel.querySelector('[data-add], .ringpick__add, button[type="submit"]');
          if (add) window.setTimeout(function () { add.focus({ preventScroll: true }); }, 320);
        });
      }

      /* Pinned from the top: nothing to watch for, and no scroll listener to
         run for the rest of the visit. Liquid already left the `hidden`
         attribute off, so this only has to not put it back. */
      if (bar.hasAttribute('data-sticky-always')) {
        bar.hidden = false;
        return;
      }

      function apply() {
        var past = panel.getBoundingClientRect().bottom < 0;
        if (past === !bar.hidden) return;
        bar.hidden = !past;
      }
      window.addEventListener('scroll', apply, { passive: true });
      window.addEventListener('resize', apply, { passive: true });
      apply();
    });
  }

  /* ----------------------------- Countdown ------------------------------
     The announcement bar can carry a deadline. Liquid renders the opening
     value, so the bar reads correctly on first paint and never jumps a
     character wider once this runs; all this does is keep it moving and decide
     what happens when it runs out.

     The deadline arrives stamped with the shop's UTC offset rather than as a
     bare date, so a visitor in another timezone counts down to the merchant's
     moment instead of to midnight wherever they happen to be. */
  function bindCountdown(scope) {
    find(scope, '[data-countdown]').forEach(function (el) {
      var end = Date.parse(el.getAttribute('data-deadline') || '');
      /* A date nothing can read is worse than no clock at all — it would sit
         there reading NaN. */
      if (isNaN(end)) { (el.closest('.anno__timer') || el).remove(); return; }

      var lastLen = -1;
      var timer = window.setInterval(tick, 1000);
      tick();

      function pad(n) { return n < 10 ? '0' + n : '' + n; }

      function tick() {
        /* The theme editor replaces the section on every edit and the old copy
           keeps ticking in the background otherwise. */
        if (!document.contains(el)) { window.clearInterval(timer); return; }

        var left = Math.floor((end - Date.now()) / 1000);
        if (left <= 0) { finish(); return; }

        var days = Math.floor(left / 86400);
        var text = (days ? days + 'd ' : '') +
                   pad(Math.floor(left % 86400 / 3600)) + 'h ' +
                   pad(Math.floor(left % 3600 / 60)) + 'm ' +
                   pad(left % 60) + 's';
        el.textContent = text;
        /* Hours, minutes and seconds are padded, so the line only changes
           width when the day count drops away — and on a phone that can be
           the difference between one line and two. */
        if (text.length !== lastLen) { lastLen = text.length; measureAnno(); }
      }

      function finish() {
        window.clearInterval(timer);
        var bar = el.closest('.anno');
        var over = el.getAttribute('data-expired');
        if (!bar) { el.remove(); }
        else if (over) { bar.querySelector('.anno__text').textContent = over; }
        else { bar.remove(); }
        measureAnno();
      }
    });
  }

  /* ----------------------------- Video play ----------------------------- */
  function bindVideoPlay(scope) {
    find(scope, '.video__play').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var frame = btn.closest('.video__frame');
        if (!frame) return;

        var file = frame.querySelector('video');
        if (file) {                            // uploaded to the admin
          frame.classList.add('is-playing');
          file.controls = true;
          var p = file.play();
          if (p && p.catch) p.catch(function () { /* autoplay blocked; controls are up */ });
          return;
        }

        var embed = frame.dataset.embed;       // YouTube / Vimeo link
        if (!embed) return;                    // nothing wired up yet
        var iframe = document.createElement('iframe');
        iframe.src = embed;
        iframe.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.title = 'ElahRing video';
        frame.appendChild(iframe);
        frame.classList.add('is-playing');
        btn.remove();
      });
    });
  }

})();
